import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import firebase from 'firebase/compat/app';
import {UserService} from '../services/user.service';
import {Project} from '../interfaces/project';
import Modal from 'bootstrap/js/dist/modal'
import {EMPTY, from, switchMap} from 'rxjs';
import {doc, getDoc, updateDoc} from 'firebase/firestore';
import {Teacher} from "../interfaces/teacher";
import {TeacherService} from "../services/teacher.service";

@Component({
  selector: 'app-project-details',
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.css'
})
export class ProjectDetailsComponent {
  projectId!: string;
  project!: Project;
  isAdmin = false;
  isApproved = true;
  isEditing = false;
  projectExists = true;
  selectedImage: string | null = null;
  teacherUser: Teacher | null = null;
  teacherRole: string | undefined = undefined;

  cards: {
    header: string;
    symbol: string;
    description: string;
    condition: boolean;
  }[] = [];

  visibilityStates = [
    'Only Me',
    'My School',
    'Everyone'
  ]

  constructor(private route: ActivatedRoute, private router: Router, public userService: UserService, private teacherService: TeacherService) { }

  async ngOnInit() {
    await this.getProjectDetails();
    this.teacherUser = await this.teacherService.getTeacher();
    this.teacherRole = this.teacherUser?.role.toLowerCase();

    this.userService.isAdmin$.subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
    });
  }

  toggleEditing() {
    this.isEditing = !this.isEditing;
  }

  // uses bootstrap's built-in modal feature
  openModal(image: string) {
    this.selectedImage = image;
    const modalElement = document.getElementById('imageModal');

    if (modalElement) {
      const modalInstance = new Modal(modalElement);
      modalInstance.show();
    }
  }

  onVisibilityChange(event: Event) {
    const newVisibility = (event.target as HTMLSelectElement).value;

    if (this.project && newVisibility !== this.project.visibility) {
      this.updateProjectVisibility(newVisibility);
    }
  }

  generateCardDetails() {
    this.cards = [
      /*{
        header: 'Composting',
        symbol: 'compost',
        description: ,
        condition: this.project.compostingSelected,
      },*/
      {
        header: 'Gardens',
        symbol: 'psychiatry',
        description: this.project.gardensDetails,
        condition: this.project.gardens === 'yes',
      },
      {
        header: 'Grants',
        symbol: 'account_balance',
        description: this.project.grantsDetails,
        condition: this.project.grants === 'yes',
      },
      {
        header: 'Indoor Growing',
        symbol: 'yard',
        description: this.project.indoorDetails,
        condition: this.project.indoorGrowing === 'yes',
      },
      {
        header: 'Recycling',
        symbol: 'recycling',
        description: this.project.recyclingDetails,
        condition: this.project.recycling === 'yes',
      },
    ];
  }

  formatDate(dateStr: string) {
    const date = new Date(dateStr);
    const str = date.toLocaleDateString();
    return str;
  }

  formatIsoString(dateStr: string) {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  async duplicateProject() {
    if (!this.project) return;

    try {
      // Fields to keep
      const keep = [
        'representativeName', 'representativeEmail', 'extraRep1Visible', 'extraRep2Visible',
        'rep2Email', 'rep3Email', 'school', 'customSchool', 'isPartOfCluster',
        'teachingLevel', 'grades', 'acknowledgement', 'projectDesc', 'projectClubs',
        'impact', 'numStudentsInProject', 'numTeachersInProject', 'eventDuration',
        'numParticipationHours', 'communityPartners', 'support', 'supportOther',
        'communityPartnersContactInfo', 'clubs', 'fieldtrip', 'grantSupplies',
        'funderAndDonorNameAndComments', 'amount', 'numCommunityMembers',
        'numParentsEngaged', 'numVolunteerHours','foodwasteToggle', 'educationalcampaign1', 'sharetable1', 'compostingSelected', 'pantry', 'fwasteaudit', 'foodOtherCheck',
        'outdoorSelected', 'fish', 'chicken', 'class', 'trails', 'greenhouse', 'compostSystem', 'pollinatorCensus', 'outdoorOtherCheck',
        'indoorSelected', 'hydroponics1', 'aquaponics1', 'aeroponics1', 'towers1', 'indoorOtherCheck',
        'WaterToggle', 'waterEducationalCampaign', 'adoptARoadWatershed', 'adoptAStreamCleanup', 'adoptAStreamMonitoring', 'streamRestoration',
        'WatershedProjectsToggle', 'greatDaysService', 'wetlandCleanup', 'yellowRiverTrashBash', 'shoreSweep', 'localWaterEvent', 'gwinnettServiceDept', 'supportCharity',
        'watershedTripsToggle', 'wasteWaterPlant', 'watersheddrinkingWaterPlant', 'theWaterTower', 'deptOfWaterResources', 'gwinnettCleanBeautiful', 'elachee', 'watershedOtherCheck',
        'recyclingSelected', 'paperAndCardboard', 'textiles1', 'plastics1', 'aluminumcans1', 'recycleOtherCheck',
        'recyclingEventsToggle', 'chipperEvent', 'earthDayEvent', 'americaRecyclesEvent', 'recyclingEventsOtherCheck',
        'renewableToggle', 'solar1', 'wind1', 'water1'
      ];

      const draftProfile: any = {};

      for (const key in this.project) {
        if (!keep.includes(key)) continue;

        draftProfile[key] = (this.project as any)[key];
      }

      await this.router.navigate(['/new-project'], {state: {draftProfile}});
    } catch (error) {
      console.error('Error duplicating project:', error);
      alert('Something went wrong while duplicating this project.');
    }
  }

  async getProjectDetails() {
    this.projectId = this.route.snapshot.paramMap.get('id')!;

    try {
      let docSnapshot = await firebase.firestore().collection('projects').doc(this.projectId).get();
      if (docSnapshot.exists) {
        const data = { id: docSnapshot.id, ...docSnapshot.data() };
        this.project = data as Project;
        this.generateCardDetails();
      } else {
        this.projectExists = false;
      }
    } catch (error) {
      console.error('Error fetching project details: ', error);
      this.projectExists = false;
    }
  }

  async updateProjectVisibility(newVisibility: string) {
    if (!this.project || !this.projectId) return;

    try {
      await firebase.firestore().collection('projects').doc(this.projectId).update({
        visibility: newVisibility
      });

      this.project.visibility = newVisibility;
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  }

  onProjectFormSubmitted(obj: {formData: Project, selectedImages: File[]}) {
    const projectData = obj.formData;

    if (projectData.id) {
      const projectRef = doc(firebase.firestore(), `projects/${projectData.id}`);
      projectData.dateEdited = new Date().toISOString();

      // updateDoc doesn't work if an id field is passed in, so extract it first
      const { id, ...idlessData } = projectData;

      from(getDoc(projectRef)).pipe(
        switchMap((pendingDoc) => {
          if (pendingDoc.exists()) {
            return from(updateDoc(projectRef, idlessData));
          }
          return EMPTY;
        })
      ).subscribe({
        next: () => {
          this.project = projectData;
          this.isEditing = false;
        },
        error: (err) => {
          alert("Something went wrong. Check console for error.");
          console.error('Error updating partner:', err);
        }
      });
    } else {
      alert("Partner has no ID.")
    }
  }

  isDeleting = false;
  deleteProject() {
    if (!confirm("Are you sure you want to delete this project?")) return;
    this.isDeleting = true;

    return firebase.firestore()
      .collection('projects')
      .doc(this.project.id)
      .set({ isDeleted: true }, { merge: true })
      .catch(err => {
        console.error(err);
        this.isDeleting = false;
      });
  }

  undeleteProject() {
    if (!confirm("Are you sure you want to undelete this project?")) return;
    this.isDeleting = true;

    return firebase.firestore()
      .collection('projects')
      .doc(this.project.id)
      .set({ isDeleted: false }, { merge: true })
      .then(() => {
        this.project.isDeleted = false;
        this.isDeleting = false;
      })
      .catch(err => {
        console.error(err);
        this.isDeleting = false;
      });
  }
}
